import { AnyEventObject, EventData, interpret, State, StateConfig } from 'xstate';
import stateMachine, { branchFlow, Context, linearFlow } from './stateMachine';
import stepRouteMapping from './stepRouteMapping';
import VueRouter from 'vue-router';
import VIEWS_NAMES from '@/router/viewsNames';

const pipeline = interpret(stateMachine);
const transitionListeners: Function[] = [];

// Instância desta aba efetivamente gravada no localStorage. Fica `null` quando o
// storage está bloqueado (WebView, navegação privada): sem isso a guarda de
// multi-aba não tem como funcionar e simplesmente não atua.
let persistedInstance: string | null = null;

const CURRENT_INSTANCE_KEY = 'currentInstance';

function readPersistedInstance(): string | null {
  try {
    return localStorage.getItem(CURRENT_INSTANCE_KEY);
  } catch {
    // Storage bloqueado: tratamos como ausência de guarda, nunca como divergência.
    return null;
  }
}

function getCurrentStep(state: State<Context>): string {
  const [currentStateMeta]: { step: string }[] = Object.values(state.meta);

  return currentStateMeta.step;
}

function navigateToStep(router: VueRouter, state: State<Context>) {
  const currentStep = getCurrentStep(state);

  const stepRoute = stepRouteMapping[currentStep];

  const currentRoute = router.currentRoute.path;

  if (stepRoute && stepRoute !== currentRoute) router.push(stepRoute);
}

// O `history` só cresce no `exit` de uma transição `NEXT`, então um estado persistido
// antigo pode chegar sem os passos que o candidato obrigatoriamente já percorreu. Como
// `canNavigateToStep` exige o passo em `history`, o menu lateral vira botão morto — o
// clique não faz nada, em silêncio. Aqui derivamos o prefixo implicado pelo passo atual.
//
// Duas decisões que impedem o reparo de virar bug:
// - **União, nunca remoção:** preserva a intenção de `resetHistory` e `setRetryHistory`.
// - **Passo de resultado não é reparado** (`retryEssay`, `success`, `failure`,
//   `pendingApproval` não estão no fluxo → índice -1 → `history` intacto). O
//   `setRetryHistory` trunca de propósito para os 4 passos comuns; reparar ali reabriria
//   os passos antigos de redação para quem está em `retryEssay`.
export function repairHistory(state: State<Context>): string[] {
  const history = state.context?.history || [];

  const [currentStateMeta]: { step?: string }[] = Object.values(state.meta || {});
  const currentStep = currentStateMeta?.step;

  const { admissionType } = state.context || {};
  const flow = [...linearFlow, ...(admissionType ? branchFlow[admissionType] : [])];

  const currentIndex = currentStep ? flow.indexOf(currentStep) : -1;

  if (currentIndex < 0) return history;

  // O passo atual ainda não foi deixado, então o implicado é o prefixo *antes* dele —
  // exatamente o que o `resetHistory` produz (em `citySelection`, `['courseSelection']`).
  return Array.from(new Set([...flow.slice(0, currentIndex), ...history]));
}

function loadPreviousState(previousState: StateConfig<Context, AnyEventObject>): State<Context> {
  const state = State.create<Context>(previousState);
  const resolvedState = stateMachine.resolveState(state);

  if (resolvedState?.context) {
    resolvedState.context = { ...resolvedState.context, history: repairHistory(resolvedState) };
  }

  return resolvedState;
}

function forcePipelineStop(router: VueRouter) {
  pipeline.stop();
  router.push({ name: VIEWS_NAMES.HOME });
}

function onTransitionHandler(router: VueRouter, currentInstance: string) {
  return (state: State<Context>) => {
    const instanceOnStorage = readPersistedInstance();

    // Só derruba o pipeline quando a guarda está de fato ativa: gravamos a instância
    // desta aba e a leitura devolveu algo. Sem storage, `getItem` devolve `null` e
    // comparar aqui chutaria o candidato para a Home em toda transição.
    const isMultiTabGuardActive = persistedInstance !== null && instanceOnStorage !== null;

    if (isMultiTabGuardActive && instanceOnStorage !== currentInstance) {
      forcePipelineStop(router);
      return;
    }

    if (state.changed) {
      navigateToStep(router, state);
      transitionListeners.forEach(cb => cb(state));
    }
  };
}

function start(router: VueRouter, previousState?: StateConfig<Context, AnyEventObject>) {
  const loadedPreviousState = previousState ? loadPreviousState(previousState) : undefined;

  const currentInstance = String(Date.now());

  try {
    localStorage.setItem(CURRENT_INSTANCE_KEY, currentInstance);
    persistedInstance = currentInstance;
  } catch {
    // Storage indisponível: o pipeline inicia normalmente, apenas sem guarda de multi-aba.
    persistedInstance = null;
  }

  const { state: currentState } = pipeline
    .onTransition(onTransitionHandler(router, currentInstance))
    .start(loadedPreviousState);

  navigateToStep(router, currentState);
}

function addTransitionListener(listener: Function, forceTrigger?: boolean) {
  const isAlreadyRegistered = transitionListeners.includes(listener);

  if (!isAlreadyRegistered) {
    if (forceTrigger) listener(pipeline.state);
    transitionListeners.push(listener);
  }
}

function removeTransitionListener(cb: Function) {
  const listenerIndex = transitionListeners.indexOf(cb);

  if (listenerIndex >= 0) {
    transitionListeners.splice(listenerIndex, 1);
  }
}

export default {
  get currentStep() {
    return getCurrentStep(pipeline.state);
  },
  get state() {
    return pipeline.state;
  },
  get initialized() {
    return pipeline.initialized;
  },
  addTransitionListener,
  forcePipelineStop,
  goTo: (step: string) => {
    if (step) pipeline.send(`GO_TO_${step.toUpperCase()}`);
  },
  loadPreviousState,
  navigateToStep,
  next: (event?: EventData) => pipeline.send('NEXT', event),
  resetHistory: (event?: EventData) => pipeline.send('RESET_HISTORY', event),
  onTransitionHandler,
  prev: (event?: EventData) => pipeline.send('PREV', event),
  removeTransitionListener,
  send: pipeline.send,
  start,
  stop: pipeline.stop,
  transitionListeners,
  __pipeline: pipeline,
};
